import React, { useState, useEffect, useRef } from 'react';
import { X, Save} from 'lucide-react';
import { toast } from 'react-hot-toast';
import api, {  getCourtTypes } from '../services/api';


interface CourtType {
  id: number;
  name: string;
  created_at: string;
  updated_at: string;
}

interface CourtTypeCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface CourtTypeEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  courtType: CourtType | null;
}

interface CourtTypeViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  courtType: CourtType | null;
}

interface CourtTypeReplaceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  courtType: CourtType | null;
}

// Court Create Modal
export const CourtTypeCreateModal: React.FC<CourtTypeCreateModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setIsSubmitting(true);
    try {
      await api.post('/api/court-types', {
        name: formData.name,
      });
      
      toast.success('Court Type created successfully');
      onSuccess();
      onClose();
      setFormData({
        name: ''
      });
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to create Court Type');
    } finally {
      setIsSubmitting(false);
    }
  };


  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-600">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Create New Court Type
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Court*
              </label>
              <input
                ref={inputRef}
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                required
                autoFocus
              />
            </div>

          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              <span>{isSubmitting ? 'Creating...' : 'Create Court'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Court Type Edit Modal
export const CourtTypeEditModal: React.FC<CourtTypeEditModalProps> = ({ isOpen, onClose, onSuccess, courtType }) => {
  const [formData, setFormData] = useState({
    name: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const editInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => editInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  useEffect(() => {
    if (courtType) {
      setFormData({
        name: courtType.name,

      });
    }
  }, [courtType]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!courtType) return;

    setIsSubmitting(true);
    try {
      await api.put(`/api/court-types/${courtType.id}`, {
        name: formData.name,
      });
      
      toast.success('Court Type updated successfully');
      onSuccess();
      onClose();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to update Court Type');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen || !courtType) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-600">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Edit Court Type
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Court Type*
              </label>
              <input
                ref={editInputRef}
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                required
                autoFocus
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              <span>{isSubmitting ? 'Updating...' : 'Update Court'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Court View Modal
export const CourtTypeViewModal: React.FC<CourtTypeViewModalProps> = ({ isOpen, onClose, courtType }) => {
  if (!isOpen || !courtType) return null;


  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-600">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Court Type Details
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* User Avatar and Basic Info */}
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0 h-16 w-16">
              <div className="h-16 w-16 rounded-full bg-blue-500 flex items-center justify-center">
                <span className="text-white text-xl font-medium">
                  {courtType.name[0]}
                </span>
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-4">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Document Type Replace Modal
  export const CourtTypeReplaceModal: React.FC<CourtTypeReplaceModalProps> = ({ isOpen, onClose, onSuccess, courtType }) => {
  
  const [courtTypesLoading, setCourtTypesLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    new_court_type: '',
    old_court_type: '',
  });

  console.log('Loading court types');
  console.log(formData);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [courtTypes, setCourtTypes] = useState<CourtType[]>([]);

  useEffect(() => {
    if (courtType) {
      setFormData({
        new_court_type: '',
        old_court_type: courtType.name,
      });
    }
    
  }, [courtType]);

  useEffect(() => {
    const loadData = async () => {

    
    setCourtTypesLoading(true);
      try {
        const courtTypesData = await getCourtTypes();
        setCourtTypes(courtTypesData);
      } catch (error) {
        console.error('Failed to load court types:', error);
        toast.error('Failed to load court types');
      } finally {
        setCourtTypesLoading(false);
      }
    };
    loadData();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!courtType) return;

    setIsSubmitting(true);
    try {
      await api.put(`/api/court-types/replace`, {
        new_court_type: formData.new_court_type,
        old_court_type: courtType.name,
      });
      
      toast.success('Court Type updated successfully');
      onSuccess();
      onClose();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to update Court Type');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen || !courtType) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-600">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Replace Court Type
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Old Court Value*
              </label>
              <input
                type="text"
                  value={formData.old_court_type}
                onChange={(e) => setFormData({ ...formData, old_court_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                required
                disabled
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              New Court Value *
            </label>
            <select
              value={formData.new_court_type}
              onChange={(e) => setFormData({ ...formData, new_court_type: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              required
            >
              <option value="">Select court type</option>
              {courtTypes.map((courtType) => (
                <option key={courtType.id} value={courtType.name}>
                  {courtType.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              <span>{isSubmitting ? 'Updating...' : 'Update Court Type'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};


